import { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@shared/api/client';
import { useUploadWalletDocument, useWalletDocuments } from '@shared/hooks/use-wallet-documents';
import type { WalletDocumentResponse } from '@shared/types/api';
import { cn } from '@/lib/cn';
import { WalletCategorySelect } from '../wallet-category-select';

interface WalletDocumentSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectDocuments: (docs: WalletDocumentResponse[]) => void;
  alreadySelectedIds?: string[];
}

export function WalletDocumentSelectorModal({
  open,
  onClose,
  onSelectDocuments,
  alreadySelectedIds = [],
}: WalletDocumentSelectorProps) {
  const { data: documents = [], isLoading, isError, error } = useWalletDocuments();
  const uploadMutation = useUploadWalletDocument();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(alreadySelectedIds));

  // Sync initial selections when open changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(alreadySelectedIds));
    }
  }, [open, alreadySelectedIds]);

  // Quick Upload Inline State
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('AADHAAR');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const filteredDocs = documents.filter(
    (d) =>
      !searchQuery.trim() ||
      d.documentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.documentType && d.documentType.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const isAllSelected = filteredDocs.length > 0 && filteredDocs.every((d) => selectedIds.has(d.id));

  function toggleSelectAll() {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const d of filteredDocs) next.add(d.id);
        return next;
      });
    }
  }

  function handleConfirmSelection() {
    const selectedDocs = documents.filter((d) => selectedIds.has(d.id));
    onSelectDocuments(selectedDocs);
    onClose();
  }

  function handleFileSelect(file: File | null) {
    if (!file) return;
    setUploadFile(file);
  }

  async function handleQuickUploadSubmit(e: React.FormEvent) {
    e.preventDefault();
    setUploadError(null);
    if (!uploadFile) {
      setUploadError('Please select a document file');
      return;
    }

    const isOther = uploadCategory.toUpperCase() === 'OTHER';
    if (isOther && !uploadName.trim()) {
      setUploadError('Document name is required when category is Other Document');
      return;
    }

    try {
      const newDoc = await uploadMutation.mutateAsync({
        file: uploadFile,
        documentName: uploadName.trim() || undefined,
        documentType: uploadCategory,
      });
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadName('');
      // Auto-select freshly uploaded document
      setSelectedIds((prev) => new Set(prev).add(newDoc.id));
      onSelectDocuments([...documents.filter((d) => selectedIds.has(d.id)), newDoc]);
      onClose();
    } catch (err) {
      setUploadError(apiErrorMessage(err, 'Failed to upload document'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Document Wallet"
      footer={
        <div className="flex items-center justify-between w-full gap-2">
          <Button variant="ghost" onClick={onClose} className="text-xs font-bold">
            Cancel
          </Button>
          {!showUploadForm && (
            <Button
              disabled={selectedIds.size === 0}
              onClick={handleConfirmSelection}
              className="bg-brand-700 hover:bg-brand-800 text-white font-bold text-xs px-4 py-2 rounded-md shadow-xs transition-colors disabled:opacity-50"
            >
              {selectedIds.size > 0
                ? `Attach ${selectedIds.size} Document${selectedIds.size > 1 ? 's' : ''} to Chat`
                : 'Select Documents'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {/* Inline Quick Upload Form */}
        {showUploadForm ? (
          <form
            onSubmit={handleQuickUploadSubmit}
            className="space-y-3 bg-gray-50 border border-gray-200 rounded-2xl p-4"
          >
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
              Upload & Save to Wallet
            </h4>
            {uploadError ? (
              <div className="rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-700 font-medium">
                {uploadError}
              </div>
            ) : null}

            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">
                Document File / Photo *
              </label>
              <input
                type="file"
                required
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-100 file:text-brand-800 hover:file:bg-brand-200"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">Category</label>
              <WalletCategorySelect value={uploadCategory} onChange={setUploadCategory} />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">
                Document Name / Identifier{' '}
                {uploadCategory.toUpperCase() === 'OTHER' ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-gray-400 font-normal lowercase">(optional)</span>
                )}
              </label>
              <input
                type="text"
                required={uploadCategory.toUpperCase() === 'OTHER'}
                placeholder={
                  uploadCategory.toUpperCase() === 'OTHER'
                    ? 'e.g. Utility Bill, Lease Agreement'
                    : 'Enter custom name (optional)'
                }
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-md border border-gray-300 focus:border-brand-600 focus:outline-none bg-white font-medium text-gray-900"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5"
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={uploadMutation.isPending}
                className="bg-brand-700 hover:bg-brand-800 text-white font-semibold text-xs px-4 py-1.5 rounded-md shadow-xs"
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload & Select for Chat'}
              </Button>
            </div>
          </form>
        ) : (
          <>
            {/* Search Input & Select All toggle */}
            <div className="flex items-center justify-between gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Filter wallet documents by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-xs rounded-md border border-gray-300 focus:border-brand-600 focus:outline-none bg-gray-50 focus:bg-white transition-colors"
                />
                <svg
                  className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {filteredDocs.length > 0 ? (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-xs font-bold text-brand-700 hover:text-brand-900 hover:underline shrink-0 px-2 py-1"
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </button>
              ) : null}
            </div>

            {/* Document List */}
            {isLoading ? (
              <p className="text-center text-xs text-gray-500 py-8">Loading wallet documents…</p>
            ) : isError ? (
              <p className="text-center text-xs text-red-600 py-4 font-medium">
                {apiErrorMessage(error)}
              </p>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 space-y-2">
                <p className="text-xs font-semibold">No documents found in wallet</p>
                <p className="text-[11px] text-gray-400">
                  Click "+ Upload New" to add Aadhaar, PAN or other documents.
                </p>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                {filteredDocs.map((doc) => {
                  const sizeKb = (Number(doc.sizeBytes) / 1024).toFixed(1);
                  const isSelected = selectedIds.has(doc.id);

                  return (
                    <div
                      key={doc.id}
                      onClick={() => toggleSelect(doc.id)}
                      className="flex items-center justify-between gap-3 p-2.5 rounded-xl cursor-pointer transition-colors hover:bg-gray-50 select-none"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer shrink-0 accent-emerald-600 pointer-events-none"
                        />
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-750 font-bold border border-brand-200/60 text-xs uppercase">
                          {doc.documentType?.slice(0, 3) || 'DOC'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-extrabold text-gray-900 truncate">
                            {doc.documentName}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate mt-0.5">
                            {doc.fileName} • {sizeKb} KB
                          </p>
                        </div>
                      </div>

                      <span
                        className={cn(
                          'text-[11px] font-bold px-2.5 py-1 rounded-md transition-colors shrink-0',
                          isSelected
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200',
                        )}
                      >
                        {isSelected ? '✓ Selected' : 'Select'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
